import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Globe, ArrowUpRight, Loader2, Building2 } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CONTACT, IMAGES } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", email: "", company: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      if (data.status === "success") {
        toast.success("Message sent — we'll be in touch shortly.");
        setForm(EMPTY);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (err) {
      toast.error("Could not send your message. Please email us directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "bg-slate-900/[0.04] border-slate-900/10 text-slate-900 placeholder:text-slate-500 rounded-xl h-12 focus-visible:ring-1 focus-visible:ring-blue-500 focus-visible:border-blue-500";

  return (
    <div data-testid="contact-page">
      <section className="relative overflow-hidden pb-8 pt-40">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_10%,rgba(0,122,255,0.16),transparent_50%)]" />
        <div className="relative z-10 mx-auto max-w-6xl px-6">
          <Reveal>
            <Overline>Contact</Overline>
            <h1 className="mt-5 max-w-4xl font-display text-5xl font-extrabold leading-[0.95] tracking-tighter sm:text-6xl lg:text-7xl">
              <span className="text-slate-900">Let&apos;s build </span>
              <span className="metallic-text">something remarkable.</span>
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 py-16">
        <div className="grid gap-8 lg:grid-cols-[1fr_1.1fr]">
          {/* Left — info */}
          <Reveal className="flex flex-col gap-6">
            <div className="relative overflow-hidden rounded-3xl border border-slate-900/10">
              <img src={IMAGES.dubai} alt="Dubai skyline" className="h-56 w-full img-tech object-cover md:h-72" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#e9eef5] via-[#e9eef5]/30 to-transparent" />
              <div className="absolute bottom-5 left-6">
                <p className="font-mono-x text-xs uppercase tracking-[0.2em] text-blue-600">Headquarters</p>
                <p className="mt-1 font-display text-2xl font-bold text-slate-900">Dubai, UAE</p>
              </div>
            </div>

            <div className="glass grid gap-5 rounded-3xl p-7">
              <a href={`mailto:${CONTACT.email}`} className="group flex items-start gap-4" data-testid="contact-email">
                <div className="chrome-orb flex h-11 w-11 items-center justify-center rounded-xl">
                  <Mail size={18} className="text-[#0a1830]" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500">Email</div>
                  <div className="text-base text-slate-900 transition-colors group-hover:text-blue-600">{CONTACT.email}</div>
                </div>
              </a>
              <div className="flex items-start gap-4">
                <div className="chrome-orb flex h-11 w-11 items-center justify-center rounded-xl">
                  <MapPin size={18} className="text-[#0a1830]" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500">Address</div>
                  <div className="text-base text-slate-900">{CONTACT.address}</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="chrome-orb flex h-11 w-11 items-center justify-center rounded-xl">
                  <Globe size={18} className="text-[#0a1830]" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500">Website</div>
                  <div className="text-base text-slate-900">{CONTACT.website}</div>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Right — form */}
          <Reveal delay={0.1}>
            <form onSubmit={submit} className="glass rounded-3xl p-7 md:p-9" data-testid="contact-form">
              <h2 className="font-display text-2xl font-bold text-slate-900">Send us a message</h2>
              <p className="mt-2 text-sm text-slate-600">We typically reply within one business day.</p>

              <div className="mt-7 grid gap-5 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="name" className="text-slate-700">Name *</Label>
                  <Input id="name" data-testid="contact-input-name" value={form.name} onChange={update("name")} placeholder="Your name" className={inputCls} />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="email" className="text-slate-700">Email *</Label>
                  <Input id="email" type="email" data-testid="contact-input-email" value={form.email} onChange={update("email")} placeholder="you@company.com" className={inputCls} />
                </div>
              </div>

              <div className="mt-5 grid gap-5 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="company" className="text-slate-700">Company</Label>
                  <Input id="company" data-testid="contact-input-company" value={form.company} onChange={update("company")} placeholder="Company (optional)" className={inputCls} />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="subject" className="text-slate-700">Subject</Label>
                  <Input id="subject" data-testid="contact-input-subject" value={form.subject} onChange={update("subject")} placeholder="What's this about?" className={inputCls} />
                </div>
              </div>

              <div className="mt-5 grid gap-2">
                <Label htmlFor="message" className="text-slate-700">Message *</Label>
                <Textarea id="message" data-testid="contact-input-message" value={form.message} onChange={update("message")} placeholder="Tell us about your project…" rows={5} className={`${inputCls} h-auto py-3`} />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-button"
                className="glossy-btn mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full px-8 py-4 text-base font-semibold text-white transition-transform duration-300 hover:scale-[1.01] disabled:cursor-not-allowed disabled:opacity-70"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Send message <ArrowUpRight size={18} />
                  </>
                )}
              </button>

              <p className="mt-4 flex items-center gap-2 text-xs text-slate-500">
                <Building2 size={13} /> Your message reaches our Dubai team directly.
              </p>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
